import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { RankItem } from "./analiseEngine";

interface RankingTableProps {
  titulo: string;
  itens: RankItem[];
  limite?: number;
  destaque?: "freq" | "atraso" | "tendencia";
}

const fmtData = (d: string | null) => {
  if (!d) return "-";
  const [y, m, dd] = d.split("-");
  return `${dd}/${m}/${y.slice(2)}`;
};

const RankingTable: React.FC<RankingTableProps> = ({ titulo, itens, limite = 15, destaque = "freq" }) => {
  const lista = itens.slice(0, limite);

  const copiar = () => {
    const txt = lista
      .map((i, idx) => `${idx + 1}. ${i.label} — ${i.freq}x • atraso ${i.atraso}`)
      .join("\n");
    navigator.clipboard.writeText(`${titulo}\n${txt}`);
    toast({ title: "Copiado!", description: `${titulo} copiado para a área de transferência.` });
  };

  if (lista.length === 0) {
    return (
      <div className="rounded-lg border border-yellow-500/20 bg-black/30 p-3">
        <p className="text-xs text-yellow-200/60">{titulo}: sem dados no recorte selecionado.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-yellow-500/20 bg-black/30 p-2 sm:p-3">
      <div className="flex items-center justify-between mb-2 gap-2">
        <p className="text-[11px] sm:text-sm font-semibold text-yellow-400">{titulo}</p>
        <Button size="sm" variant="ghost" onClick={copiar} className="h-7 px-2 text-yellow-300 hover:text-yellow-100">
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="space-y-1">
        {lista.map((i, idx) => (
          <div
            key={i.key}
            className="flex items-center justify-between gap-2 rounded-md bg-black/40 px-2 py-1.5 border border-yellow-500/10"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] text-yellow-200/40 w-4 shrink-0">{idx + 1}</span>
              <span className="text-[11px] sm:text-sm font-mono text-yellow-100 truncate">{i.label}</span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Badge
                className={`text-[9px] sm:text-[10px] ${
                  destaque === "freq"
                    ? "bg-yellow-500/20 text-yellow-200 border-yellow-500/30"
                    : "bg-black/40 text-yellow-200/70 border-yellow-500/20"
                }`}
              >
                {i.freq}x
              </Badge>
              <Badge
                className={`text-[9px] sm:text-[10px] ${
                  destaque === "atraso"
                    ? "bg-red-500/20 text-red-200 border-red-500/40"
                    : "bg-black/40 text-yellow-200/60 border-yellow-500/20"
                }`}
              >
                atraso {i.atraso}
              </Badge>
              {destaque === "tendencia" && (
                <Badge className="bg-green-500/20 text-green-200 border-green-500/40 text-[9px] sm:text-[10px]">
                  {i.tendencia.toFixed(1)}x
                </Badge>
              )}
              <span className="hidden sm:inline text-[10px] text-yellow-200/40">{fmtData(i.ultimaData)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RankingTable;
